"use client";

import { Form, Button, message, Collapse, Alert } from "antd";
import { useLayoutEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router";
import { useForm } from "antd/es/form/Form";
import { IconArrowNarrowLeft, IconChevronDown } from "@tabler/icons-react";

import { EventData } from "../../../models/event";
import { useAPI } from "../../../hooks/useAPI";
import { ENDPOINTS } from "../../../constants/endpoints.constant";
import { updateCall } from "../../../constants/apicalls.constant";
import { ROUTES } from "../../../constants/routes.constant";
import { setSelectedEventList } from "../../../store/slices/event.slice";
import { transformImageFieldsToUIDs } from "../../../utils/TransfromImageFieldsToUIDs";
import { RootState } from "../../../store/store";
import { setEditEvent } from "../../../store/slices/eventedit.slice";
import {
  isEventSetupReadOnly,
  isEventUnderAdminReview,
} from "../../../utils/eventEditAccess";

import Loader from "../../../components/Loader";
import "../custom-form.css";

import { customFormConfig } from "./form";
import { getSectionItems } from "../renderSection";
import { CustomFormSection } from "../types";

const buildParentControlledFields = (
  preSection: CustomFormSection,
  sectionEnabled: boolean,
  sectionRequired: boolean,
  sectionCategories: string[],
) =>
  preSection.fields.map((field) => ({
    ...field,
    enabled: sectionEnabled,
    is_required: field.fixedRequired ? true : sectionRequired,
    enabled_for_categories: sectionCategories,
  }));

const getParentControlledSectionState = (
  preSection: CustomFormSection,
  savedSection: CustomFormSection | undefined,
  hasExistingConfig: boolean,
  allCategoryIds: string[],
) => {
  const savedFields = savedSection?.fields ?? [];
  const anyFieldEnabled = savedFields.some((f) => f.enabled);
  const sectionEnabled = hasExistingConfig
    ? !!savedSection?.enabled || anyFieldEnabled
    : false;
  const sectionRequired = savedFields.some((f) => f.required);
  const sectionCategories =
    savedFields.find((f) => f.enabledForCategories?.length)
      ?.enabledForCategories ?? allCategoryIds;

  return {
    enabled: sectionEnabled,
    is_required: sectionRequired,
    enabled_for_categories: sectionCategories,
    fields: buildParentControlledFields(
      preSection,
      sectionEnabled,
      sectionRequired,
      sectionCategories,
    ),
  };
};

const syncParentControlledSection = (
  form: ReturnType<typeof useForm>[0],
  sectionId: string,
  allCategoryIds: string[],
) => {
  const preSection = customFormConfig.sections.find((s) => s.id === sectionId);
  if (!preSection?.parentControlled) return;

  const sectionData = form.getFieldValue(sectionId) ?? {};
  const sectionEnabled = !!sectionData.enabled;
  const sectionRequired = !!sectionData.is_required;
  const sectionCategories =
    sectionData.enabled_for_categories?.length > 0
      ? sectionData.enabled_for_categories
      : allCategoryIds;

  form.setFieldsValue({
    [sectionId]: {
      ...sectionData,
      enabled_for_categories: sectionCategories,
      fields: buildParentControlledFields(
        preSection,
        sectionEnabled,
        sectionRequired,
        sectionCategories,
      ),
    },
  });
};

const CustomFormPage = () => {
  const event = useSelector((state: RootState) => state.event);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [form] = useForm();
  const pathname = location.pathname;
  const tickets = event?.tickets ?? [];
  const allCategoryIds = tickets.map((t) => t.id ?? t.name);
  const showProgressAndButtons = pathname.includes("event-custom-form");

  const [activeKeys, setActiveKeys] = useState<string[]>(() =>
    customFormConfig.sections
      .filter((section) => section.fixedEnabled)
      .map((section) => section.id),
  );

  const sectionItems = getSectionItems(customFormConfig.sections, tickets);
  const formLocked =
    isEventUnderAdminReview(event) || isEventSetupReadOnly(event);

  const { loading, trigger } = useAPI<{
    status: boolean;
    message: string;
    data: EventData;
  }>(ENDPOINTS.event);

  /* ================= INIT FORM ================= */
  useLayoutEffect(() => {
    const existingSections = (event?.form_config ?? []) as CustomFormSection[];
    const hasExistingConfig = existingSections.length > 0;

    const sections: Record<string, any> = {};

    customFormConfig.sections.forEach((preSection) => {
      const savedSection = existingSections.find((s) => s.id === preSection.id);

      if (preSection.parentControlled) {
        sections[preSection.id] = getParentControlledSectionState(
          preSection,
          savedSection,
          hasExistingConfig,
          allCategoryIds,
        );
        return;
      }

      const fields = preSection.fields.map((field) => {
        const savedField = savedSection?.fields?.find(
          (f) => f.key === field.key,
        );

        let initialCategories = savedField?.enabledForCategories ?? allCategoryIds;

        let isEnabled = field.fixedRequired && !field.canToggleEnabled ? true : !!field.enabled;
        if (field.fixedEnabled && !field.canToggleEnabled) {
          isEnabled = true;
        } else if (hasExistingConfig) {
          isEnabled = savedSection ? !!savedField?.enabled : false;
        }

        let isRequired = !!field.required;
        if (field.fixedRequired) {
          isRequired = true;
        } else if (savedField) {
          isRequired = !!savedField.required;
        }

        return {
          ...field,
          enabled: isEnabled,
          is_required: isRequired,
          enabled_for_categories: initialCategories,
        };
      });

      const isSectionEnabled =
        preSection.fixedEnabled ||
        (hasExistingConfig && savedSection
          ? !!savedSection.enabled
          : fields.some((f) => f.enabled));

      sections[preSection.id] = {
        enabled: isSectionEnabled,
        fields,
      };
    });

    form.setFieldsValue(sections);
  }, [event?.form_config, tickets, form]);

  /* ================= SAVE ================= */
  const onSave = async (_values: any) => {
    const allFormValues = form.getFieldsValue(true);

    const formConfig: CustomFormSection[] = customFormConfig.sections
      .map((configSection) => {
        const sectionId = configSection.id;
        const sectionData = allFormValues[sectionId];

        const existingSection = (
          event?.form_config as CustomFormSection[]
        )?.find((s) => s.id === sectionId);

        if (configSection.parentControlled) {
          const sectionEnabled = !!sectionData?.enabled;
          if (!sectionEnabled) return null;

          const isRequired = !!sectionData?.is_required;
          const enabledForCategories =
            sectionData?.enabled_for_categories?.length > 0
              ? sectionData.enabled_for_categories
              : allCategoryIds;

          const fields = configSection.fields.map((configField) => ({
            ...configField,
            enabled: true,
            required: configField.fixedRequired || isRequired,
            enabledForCategories: enabledForCategories,
          }));

          return {
            id: sectionId,
            title: configSection.title,
            enabled: true,
            fields,
          };
        }

        const filteredFields = configSection.fields
          .map((configField, index) => {
            const fieldData = sectionData?.fields?.[index];
            const existingField = existingSection?.fields?.find(
              (f) => f.key === configField.key,
            );

            const isFieldEnabled =
              (configField.fixedEnabled && !configField.canToggleEnabled) ||
              (fieldData?.enabled !== undefined
                ? !!fieldData.enabled
                : !!existingField?.enabled);
            if (!isFieldEnabled) return null;

            const isRequired =
              configField.fixedRequired ||
              (fieldData?.is_required !== undefined
                ? !!fieldData.is_required
                : !!existingField?.required);

            const enabledForCategories = configField.fixedEnabled
              ? allCategoryIds
              : (fieldData?.enabled_for_categories !== undefined
                  ? fieldData.enabled_for_categories
                  : (existingField?.enabledForCategories ?? []));

            return {
              ...configField,
              enabled: true,
              required: isRequired,
              enabledForCategories,
            };
          })
          .filter((f): f is any => f !== null);

        if (filteredFields.length === 0) return null;

        return {
          id: sectionId,
          title: configSection.title,
          enabled: true,
          fields: filteredFields,
        };
      })
      .filter((s): s is CustomFormSection => s !== null);

    let valueToSend: EventData = {
      ...event,
      is_custom_form: true,
    };

    if (id) {
      valueToSend = transformImageFieldsToUIDs<EventData>(valueToSend);
    }

    valueToSend.form_config = formConfig;

    if (event?.status) {
      valueToSend.status = event.status;
    }

    if (id) {
      if (!Array.isArray(valueToSend.steps_info)) {
        valueToSend.steps_info = ["custom_form"];
      } else if (!valueToSend.steps_info.includes("custom_form")) {
        valueToSend.steps_info = ["custom_form", ...valueToSend.steps_info];
      }
    }

    if (!id) {
      message.error("Event ID missing");
      return;
    }

    const res = await trigger(updateCall<EventData>(id, valueToSend));

    if (res?.status) {
      dispatch(setSelectedEventList(res.data));
      dispatch(setEditEvent({ editEvent: false }));
      message.success("Registration form saved successfully");
      navigate(ROUTES.REVIEW_AND_PUBLISH.path.replace(":id", id));
    } else {
      message.error(res?.message || "Failed to update event");
    }
  };

  return (
    <Loader spinning={loading}>
    <div className="p-24">
      {showProgressAndButtons && (
        <div className="flex items-center gap-24 mb-24">
          <Button
            shape="circle"
            icon={<IconArrowNarrowLeft size={18} />}
            variant="filled"
            color="default"
            className="border! shrink-0"
            onClick={() => navigate(`/add-event-ticket/${id}`)}
          />
          <div>
            <h2 className="text-[18px] font-semibold text-[#1f2937] leading-tight">
              Registration Form
            </h2>
            <p className="text-[13px] text-[#6b7280] mt-2">
              Configure what information participants provide when registering
            </p>
          </div>
        </div>
      )}

      <div className="w-full max-w-3xl mx-auto">
        {isEventSetupReadOnly(event) && (
          <Alert
            className="!mb-16"
            type="info"
            showIcon
            message="Registration form is read-only"
            description="This event is completed or archived. You can view settings but cannot change them."
          />
        )}
        <fieldset
          disabled={formLocked}
          style={{ border: "none", padding: 0, margin: 0 }}
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onSave}
            disabled={formLocked}
            onValuesChange={(changed) => {
              const sectionId = Object.keys(changed)[0];
              if (
                customFormConfig.sections.find(
                  (s) => s.id === sectionId && s.parentControlled,
                )
              ) {
                syncParentControlledSection(form, sectionId, allCategoryIds);
              }
            }}
          >
            <Collapse
              collapsible="icon"
              defaultActiveKey={activeKeys}
              activeKey={activeKeys}
              onChange={(keys) =>
                setActiveKeys(typeof keys === "string" ? [keys] : keys)
              }
              ghost
              expandIconPosition="end"
              items={sectionItems}
              expandIcon={({ isActive }) => (
                <IconChevronDown
                  size={16}
                  style={{
                    transform: isActive ? "rotate(180deg)" : "rotate(0)",
                    transition: "transform 0.2s ease",
                    display: "flex",
                    alignItems: "center !important",
                    alignSelf: "center",
                  }}
                />
              )}
              className="custom-form-collapse"
            />

            <div className="flex justify-end gap-24 pt-20 pb-6 border-t border-[#e5e7eb] mt-5">
              <Button onClick={() => navigate(`/add-event-ticket/${id}`)}>
                Back
              </Button>
              {!formLocked && (
              <Button
                type="primary"
                htmlType="submit"
              >
                {event?.status === "draft"
                  ? "Save & Continue"
                  : "Update Form"}
              </Button>
              )}
            </div>
          </Form>
        </fieldset>
      </div>
    </div>
    </Loader>
  );
};
export default CustomFormPage;
